/* eslint-disable @typescript-eslint/naming-convention */
import { errors } from '@vinejs/vine'
import { Exception } from '@poppinss/utils'
import * as libErrors from '@nhtio/lucid-resourceful/errors'
import { joi, ValidationError as JoiValidationError } from '@nhtio/lucid-resourceful/joi'
import type { Schema } from '@nhtio/lucid-resourceful/joi'

export type VineValidationError = InstanceType<typeof errors.E_VALIDATION_ERROR>

const passesSchema = (schema: Schema, value: unknown): boolean => {
  const { error } = schema.validate(value, { abortEarly: true })
  return !error
}

export const isError = (value: unknown): value is Error => {
  const schema = joi.alternatives(
    joi.object().instance(Error),
    joi.function().instance(Error),
    joi
      .object({
        message: joi.string().required(),
        stack: joi.string().optional(),
      })
      .unknown(true)
  )
  return passesSchema(schema, value)
}

export const isException = (value: unknown): value is Exception => {
  if (!isError(value)) {
    return false
  }
  const isLibError = Object.values(libErrors).some((error) => value instanceof error)
  if (isLibError) {
    return true
  }
  const schema = joi.alternatives(
    joi.object().instance(Exception),
    joi.function().instance(Exception),
    joi
      .object({
        message: joi.string().required(),
        stack: joi.string().optional(),
        code: joi.string().optional(),
        status: joi.number().optional(),
        help: joi.string().optional(),
      })
      .unknown(true)
  )
  return passesSchema(schema, value)
}

export const isJoiValidationError = (value: unknown): value is JoiValidationError => {
  const schema = joi.alternatives(
    joi.object().instance(JoiValidationError as any),
    joi.function().instance(JoiValidationError as any),
    joi
      .object({
        message: joi.string().required(),
        details: joi
          .array()
          .items(
            joi.object({
              message: joi.string().required(),
              path: joi.array().items(joi.alternatives(joi.string(), joi.number())).required(),
              type: joi.string().required(),
              context: joi.object().unknown(true).required(),
            })
          )
          .required(),
      })
      .unknown(true)
  )
  return passesSchema(schema, value)
}

export const isVineValidationError = (value: unknown): value is VineValidationError => {
  const schema = joi.alternatives(
    joi.object<VineValidationError>().instance(errors.E_VALIDATION_ERROR),
    joi
      .object<VineValidationError>({
        messages: joi.any().required(),
        status: joi.number().integer().min(400).max(599),
        code: joi.string().required(),
      })
      .unknown(true)
  )
  return passesSchema(schema, value)
}

export const isNotImplementedError = (error: Error): boolean => {
  return (
    'message' in error &&
    'string' === typeof error.message &&
    (error.message.includes('not implemented') ||
      error.message.includes('NotImplementedException') ||
      error.message.includes('NotImplementedError') ||
      Boolean(error.message.match(/not\s+implemented/i)))
  )
}

export const normalizeError = (error: unknown): unknown => {
  if (
    isVineValidationError(error) ||
    isJoiValidationError(error) ||
    (!isError(error) && !isException(error))
  ) {
    return error
  }
  // at this point, we know it's an Error or Exception
  const errorMessage = error.message?.toLowerCase() || 'An error occurred'
  const errorCode =
    error instanceof Exception ? error.code?.toUpperCase() || 'E_RUNTIME_ERROR' : 'E_RUNTIME_ERROR'
  const errorNumber =
    error instanceof Exception ? error.status || 500 : isNotImplementedError(error) ? 501 : 500

  if (
    errorCode === 'ER_DUP_ENTRY' ||
    errorNumber === 1062 ||
    errorCode === '23505' ||
    errorMessage.includes('duplicate key') ||
    errorMessage.includes('unique constraint') ||
    errorMessage.includes('constraint_unique') ||
    errorMessage.includes('duplicate entry') ||
    errorMessage.includes('sqlite_constraint_unique')
  ) {
    return new UNIQUE_CONSTRAINT_VIOLATION(error)
  }
  if (
    errorCode === 'ER_ROW_IS_REFERENCED_2' ||
    errorCode === 'ER_NO_REFERENCED_ROW_2' ||
    errorNumber === 1451 ||
    errorNumber === 1452 ||
    errorCode === '23503' ||
    errorMessage.includes('foreign key constraint') ||
    errorMessage.includes('violates foreign key') ||
    errorMessage.includes('sqlite_constraint_foreignkey')
  ) {
    return new FOREIGN_KEY_VIOLATION(error)
  }
  if (
    errorCode === 'ER_BAD_NULL_ERROR' ||
    errorNumber === 1048 ||
    errorCode === '23502' ||
    errorMessage.includes('cannot be null') ||
    errorMessage.includes('violates not-null constraint') ||
    errorMessage.includes('sqlite_constraint_notnull')
  ) {
    return new NOT_NULL_VIOLATION(error)
  }
  if (
    errorCode === 'ER_CONSTRAINT_FAILED' ||
    errorNumber === 4025 ||
    errorCode === '23514' ||
    errorMessage.includes('check constraint') ||
    errorMessage.includes('sqlite_constraint_check')
  ) {
    return new CHECK_CONSTRAINT_VIOLATION(error)
  }
  if (
    errorCode === 'ER_DATA_TOO_LONG' ||
    errorCode === 'ER_TRUNCATED_WRONG_VALUE' ||
    errorNumber === 1406 ||
    errorNumber === 1265 ||
    errorCode === '22001' ||
    errorCode === '22003' ||
    errorMessage.includes('data too long') ||
    errorMessage.includes('value too long') ||
    errorMessage.includes('out of range')
  ) {
    return new DATA_VALIDATION_ERROR(error)
  }
  if (
    errorCode === 'ER_TRUNCATED_WRONG_VALUE_FOR_FIELD' ||
    errorNumber === 1366 ||
    errorCode === '22P02' ||
    errorCode === '42804' ||
    errorMessage.includes('incorrect') ||
    errorMessage.includes('invalid input syntax')
  ) {
    return new INVALID_DATA_TYPE(error)
  }
  if (
    errorCode === 'ER_LOCK_DEADLOCK' ||
    errorNumber === 1213 ||
    errorCode === '40P01' ||
    errorMessage.includes('deadlock')
  ) {
    return new DEADLOCK(error)
  }
  if (
    errorCode === 'ER_LOCK_WAIT_TIMEOUT' ||
    errorNumber === 1205 ||
    errorCode === '55P03' ||
    errorMessage.includes('lock wait timeout')
  ) {
    return new LOCK_TIMEOUT(error)
  }
  if (
    errorCode === 'ER_BAD_FIELD_ERROR' ||
    errorCode === 'ER_NO_SUCH_TABLE' ||
    errorNumber === 1054 ||
    errorNumber === 1146 ||
    errorCode === '42703' ||
    errorCode === '42P01' ||
    errorMessage.includes('unknown column') ||
    errorMessage.includes('no such table') ||
    errorMessage.includes('no such column') ||
    errorMessage.includes('does not exist')
  ) {
    return new SCHEMA_ERROR(error)
  }
  if (
    errorCode === 'ECONNREFUSED' ||
    errorCode === 'ETIMEDOUT' ||
    errorCode === 'ENOTFOUND' ||
    errorMessage.includes('connection') ||
    errorMessage.includes('connect econnrefused')
  ) {
    return new DATABASE_CONNECTION_ERROR(error)
  }
  if (
    errorCode === 'ER_TABLEACCESS_DENIED_ERROR' ||
    errorCode === 'ER_COLUMNACCESS_DENIED_ERROR' ||
    errorNumber === 1142 ||
    errorNumber === 1143 ||
    errorCode === '42501' ||
    errorMessage.includes('permission denied') ||
    errorMessage.includes('access denied')
  ) {
    return new PERMISSION_DENIED(error)
  }
  if (errorCode === '40001' || errorMessage.includes('serialization failure')) {
    return new SERIALIZATION_FAILURE(error)
  }
  if (
    errorCode === 'ER_PARSE_ERROR' ||
    errorNumber === 1064 ||
    errorCode === '42601' ||
    errorMessage.includes('syntax error')
  ) {
    return new QUERY_SYNTAX_ERROR(error)
  }
  if (
    errorCode === 'ER_DIVISION_BY_ZERO' ||
    errorNumber === 1365 ||
    errorCode === '22012' ||
    errorMessage.includes('division by zero')
  ) {
    return new DIVISION_BY_ZERO(error)
  }
  return error
}

export class UNIQUE_CONSTRAINT_VIOLATION extends Exception {
  static status = 422
  static code = 'UNIQUE_CONSTRAINT_VIOLATION'

  constructor(reason: Exception | Error) {
    super('A record with the same unique key already exists', {
      code: 'UNIQUE_CONSTRAINT_VIOLATION',
      status: 422,
      cause: reason instanceof Error ? reason : undefined,
    })
  }
}

export class FOREIGN_KEY_VIOLATION extends Exception {
  static status = 422
  static code = 'FOREIGN_KEY_VIOLATION'

  constructor(reason: Exception | Error) {
    super('Referenced record does not exist or cannot be deleted due to existing references', {
      code: 'FOREIGN_KEY_VIOLATION',
      status: 422,
      cause: reason instanceof Error ? reason : undefined,
    })
  }
}

export class NOT_NULL_VIOLATION extends Exception {
  static status = 422
  static code = 'NOT_NULL_VIOLATION'

  constructor(reason: Exception | Error) {
    super('Required field cannot be empty', {
      code: 'NOT_NULL_VIOLATION',
      status: 422,
      cause: reason instanceof Error ? reason : undefined,
    })
  }
}

export class CHECK_CONSTRAINT_VIOLATION extends Exception {
  static status = 422
  static code = 'CHECK_CONSTRAINT_VIOLATION'

  constructor(reason: Exception | Error) {
    super('Value does not meet validation requirements', {
      code: 'CHECK_CONSTRAINT_VIOLATION',
      status: 422,
      cause: reason instanceof Error ? reason : undefined,
    })
  }
}

export class DATA_VALIDATION_ERROR extends Exception {
  static status = 422
  static code = 'DATA_VALIDATION_ERROR'

  constructor(reason: Exception | Error) {
    super('Data exceeds allowed length or is out of valid range', {
      code: 'DATA_VALIDATION_ERROR',
      status: 422,
      cause: reason instanceof Error ? reason : undefined,
    })
  }
}

export class INVALID_DATA_TYPE extends Exception {
  static status = 422
  static code = 'INVALID_DATA_TYPE'

  constructor(reason: Exception | Error) {
    super('Invalid data type for field', {
      code: 'INVALID_DATA_TYPE',
      status: 422,
      cause: reason instanceof Error ? reason : undefined,
    })
  }
}

export class DEADLOCK extends Exception {
  static status = 409
  static code = 'DEADLOCK'

  constructor(reason: Exception | Error) {
    super('Database deadlock detected. Please retry the operation', {
      code: 'DEADLOCK',
      status: 409,
      cause: reason instanceof Error ? reason : undefined,
    })
  }
}

export class LOCK_TIMEOUT extends Exception {
  static status = 408
  static code = 'LOCK_TIMEOUT'

  constructor(reason: Exception | Error) {
    super('Database lock timeout. Please retry the operation', {
      code: 'LOCK_TIMEOUT',
      status: 408,
      cause: reason instanceof Error ? reason : undefined,
    })
  }
}

export class SCHEMA_ERROR extends Exception {
  static status = 500
  static code = 'SCHEMA_ERROR'

  constructor(reason: Exception | Error) {
    super('Database schema error', {
      code: 'SCHEMA_ERROR',
      status: 500,
      cause: reason instanceof Error ? reason : undefined,
    })
  }
}

export class DATABASE_CONNECTION_ERROR extends Exception {
  static status = 503
  static code = 'DATABASE_CONNECTION_ERROR'

  constructor(reason: Exception | Error) {
    super('Database connection failed', {
      code: 'DATABASE_CONNECTION_ERROR',
      status: 503,
      cause: reason instanceof Error ? reason : undefined,
    })
  }
}

export class PERMISSION_DENIED extends Exception {
  static status = 403
  static code = 'PERMISSION_DENIED'

  constructor(reason: Exception | Error) {
    super('Database operation not permitted', {
      code: 'PERMISSION_DENIED',
      status: 403,
      cause: reason instanceof Error ? reason : undefined,
    })
  }
}

export class SERIALIZATION_FAILURE extends Exception {
  static status = 409
  static code = 'SERIALIZATION_FAILURE'

  constructor(reason: Exception | Error) {
    super('Transaction serialization failure. Please retry the operation', {
      code: 'SERIALIZATION_FAILURE',
      status: 409,
      cause: reason instanceof Error ? reason : undefined,
    })
  }
}

export class QUERY_SYNTAX_ERROR extends Exception {
  static status = 500
  static code = 'QUERY_SYNTAX_ERROR'

  constructor(reason: Exception | Error) {
    super('Database query syntax error', {
      code: 'QUERY_SYNTAX_ERROR',
      status: 500,
      cause: reason instanceof Error ? reason : undefined,
    })
  }
}

export class DIVISION_BY_ZERO extends Exception {
  static status = 422
  static code = 'DIVISION_BY_ZERO'

  constructor(reason: Exception | Error) {
    super('Invalid calculation: division by zero', {
      code: 'DIVISION_BY_ZERO',
      status: 422,
      cause: reason instanceof Error ? reason : undefined,
    })
  }
}
